import { WEB_BASE_URL } from '../config/appVariant';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  ActivityIndicator,
  Platform,
  Animated,
  PanResponder,
  Dimensions,
  ScrollView,
} from 'react-native';
import * as Sharing from 'expo-sharing';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../styles/ThemeProvider';
import ShareCardTemplate from './ShareCardTemplate';
import { trackShareEvent } from '../utils/analytics';
import { createShareCard } from '../lib/shareCards';

// Conditionally import ViewShot only on native
let ViewShot: any = null;
if (Platform.OS !== 'web') {
  ViewShot = require('react-native-view-shot').default;
}

const { height: SCREEN_HEIGHT } = Dimensions.get('window');
const SHEET_HEIGHT = SCREEN_HEIGHT * 0.82;

interface TopItem {
  name: string;
  value: number;
}

interface ShareCardGeneratorProps {
  visible: boolean;
  onClose: () => void;
  totalValue: number;
  itemCount: number;
  topItems?: TopItem[];
  userName?: string;
}

export default function ShareCardGenerator({
  visible,
  onClose,
  totalValue,
  itemCount,
  topItems = [],
  userName,
}: ShareCardGeneratorProps) {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        overlay: {
          flex: 1,
          justifyContent: 'flex-end',
        },
        backdrop: {
          ...StyleSheet.absoluteFillObject,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        },
        sheet: {
          height: SHEET_HEIGHT,
          backgroundColor: theme.colors.backgroundElevated,
          borderTopLeftRadius: theme.radius.radiusLg,
          borderTopRightRadius: theme.radius.radiusLg,
          overflow: 'hidden',
          borderTopWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
        },
        handleContainer: {
          paddingVertical: theme.spacing.md,
          alignItems: 'center',
        },
        handle: {
          width: 40,
          height: 4,
          backgroundColor: theme.colors.textTertiary,
          borderRadius: 2,
        },
        scrollContent: {
          flex: 1,
        },
        scrollContentContainer: {
          paddingHorizontal: theme.spacing.xl,
          paddingBottom: theme.spacing.xl,
          alignItems: 'center',
        },
        previewContainer: {
          alignItems: 'center',
          marginTop: theme.spacing.sm,
        },
        errorText: {
          ...theme.typography.sm,
          color: theme.colors.destructive,
          textAlign: 'center',
          marginTop: theme.spacing.lg,
        },
        shareOptions: {
          borderTopWidth: theme.borders.borderWidth,
          borderTopColor: theme.borders.borderColor,
          paddingVertical: theme.spacing.xl,
          paddingBottom: Platform.OS === 'ios' ? 34 : theme.spacing.xl,
        },
        shareOptionsTitle: {
          ...theme.typography.xs,
          color: theme.colors.textTertiary,
          textTransform: 'uppercase',
          letterSpacing: 1.2,
          marginBottom: theme.spacing.lg,
          paddingHorizontal: theme.spacing.xl,
        },
        shareIconsContainer: {
          paddingHorizontal: theme.spacing.lg,
          gap: theme.spacing.sm,
        },
        shareOption: {
          alignItems: 'center',
          width: 72,
        },
        shareIconCircle: {
          width: 56,
          height: 56,
          borderRadius: 28,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: theme.spacing.sm,
        },
        shareIconCircleActive: {
          backgroundColor: theme.colors.accent,
          borderColor: theme.colors.accent,
        },
        shareOptionLabel: {
          ...theme.typography.xs,
          color: theme.colors.textSecondary,
          textAlign: 'center',
        },
      }),
    [theme],
  );
  const viewShotRef = useRef<any>(null);
  const webCardRef = useRef<HTMLDivElement>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isCreatingLink, setIsCreatingLink] = useState(false);

  const translateY = useRef(new Animated.Value(SHEET_HEIGHT)).current;

  // Pan responder for swipe to dismiss
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: (_, gestureState) => {
        return gestureState.dy > 10;
      },
      onPanResponderMove: (_, gestureState) => {
        if (gestureState.dy > 0) {
          translateY.setValue(gestureState.dy);
        }
      },
      onPanResponderRelease: (_, gestureState) => {
        if (gestureState.dy > 100 || gestureState.vy > 0.5) {
          closeSheet();
        } else {
          Animated.spring(translateY, {
            toValue: 0,
            useNativeDriver: true,
            tension: 100,
            friction: 10,
          }).start();
        }
      },
    })
  ).current;

  useEffect(() => {
    if (visible) {
      Animated.spring(translateY, {
        toValue: 0,
        useNativeDriver: true,
        tension: 65,
        friction: 11,
      }).start();
    }
  }, [visible]);

  const closeSheet = () => {
    Animated.timing(translateY, {
      toValue: SHEET_HEIGHT,
      duration: 250,
      useNativeDriver: true,
    }).start(() => {
      onClose();
    });
  };

  const captureWebImage = async (): Promise<string> => {
    if (!webCardRef.current) {
      throw new Error('Card element not found');
    }

    const html2canvas = (await import('html2canvas')).default;
    const canvas = await html2canvas(webCardRef.current, {
      backgroundColor: theme.colors.backgroundElevated,
      scale: 2,
    });
    return canvas.toDataURL('image/png');
  };

  const captureNativeImage = async (): Promise<string> => {
    if (!viewShotRef.current?.capture) {
      throw new Error('ViewShot not available');
    }
    return await viewShotRef.current.capture();
  };

  const handleShare = async () => {
    setIsSharing(true);
    setError(null);

    try {
      let uri: string;

      if (Platform.OS === 'web') {
        uri = await captureWebImage();

        const link = document.createElement('a');
        link.href = uri;
        link.download = 'my-cache-value.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        trackShareEvent({
          type: 'portfolio_card_download',
          totalValue,
          itemCount,
        });
      } else {
        uri = await captureNativeImage();

        const isAvailable = await Sharing.isAvailableAsync();
        if (isAvailable) {
          await Sharing.shareAsync(uri, {
            mimeType: 'image/png',
            dialogTitle: 'Share your Cache value',
            UTI: 'public.png',
          });

          trackShareEvent({
            type: 'portfolio_card',
            totalValue,
            itemCount,
          });
        } else {
          throw new Error('Sharing is not available on this device');
        }
      }

      closeSheet();
    } catch (err) {
      console.error('Share error:', err);
      setError(err instanceof Error ? err.message : 'Failed to share');
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyLink = async () => {
    setIsCreatingLink(true);
    setError(null);

    try {
      // Create a public share card if we don't have one yet
      let url = shareUrl;
      if (!url) {
        const result = await createShareCard({
          totalValue,
          itemCount,
          topItems,
          userName,
        });

        if (result) {
          url = result.url;
          setShareUrl(url);
        } else {
          // Fallback to basic share text
          url = WEB_BASE_URL;
        }
      }

      const shareText = `Check out my collection worth $${totalValue.toLocaleString()} on Cache! ${url}`;

      if (Platform.OS === 'web') {
        await navigator.clipboard.writeText(shareText);
      } else {
        try {
          const Clipboard = require('expo-clipboard');
          await Clipboard.setStringAsync(shareText);
        } catch {
          // Fallback - just show the share sheet
          handleShare();
          return;
        }
      }

      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);

      trackShareEvent({
        type: 'copy_link',
        totalValue,
        itemCount,
      });
    } catch (err) {
      console.error('Error creating share link:', err);
      setError('Failed to create share link');
    } finally {
      setIsCreatingLink(false);
    }
  };

  const getOrCreateShareUrl = async (): Promise<string> => {
    if (shareUrl) return shareUrl;

    const result = await createShareCard({
      totalValue,
      itemCount,
      topItems,
      userName,
    });

    if (result) {
      setShareUrl(result.url);
      return result.url;
    }

    return WEB_BASE_URL;
  };

  const handleShareToMessages = async () => {
    setIsCreatingLink(true);
    setError(null);

    try {
      const url = await getOrCreateShareUrl();
      const shareText = `Check out my collection worth $${totalValue.toLocaleString()} on Cache! ${url}`;

      if (Platform.OS === 'web') {
        // On web, try Web Share API or fall back to copy
        if (navigator.share) {
          await navigator.share({
            title: 'My Cache Collection',
            text: shareText,
            url: url,
          });
        } else {
          window.open(`sms:?body=${encodeURIComponent(shareText)}`, '_blank');
        }
      } else {
        // On native, open SMS with pre-filled text
        const Linking = require('react-native').Linking;
        const smsUrl = Platform.OS === 'ios'
          ? `sms:&body=${encodeURIComponent(shareText)}`
          : `sms:?body=${encodeURIComponent(shareText)}`;
        await Linking.openURL(smsUrl);
      }

      trackShareEvent({
        type: 'share_messages',
        totalValue,
        itemCount,
      });
    } catch (err) {
      console.error('Error sharing to messages:', err);
    } finally {
      setIsCreatingLink(false);
    }
  };

  const handleShareToWhatsApp = async () => {
    setIsCreatingLink(true);
    setError(null);

    try {
      const url = await getOrCreateShareUrl();
      const shareText = `Check out my collection worth $${totalValue.toLocaleString()} on Cache! ${url}`;

      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

      if (Platform.OS === 'web') {
        window.open(whatsappUrl, '_blank');
      } else {
        const Linking = require('react-native').Linking;
        await Linking.openURL(whatsappUrl);
      }

      trackShareEvent({
        type: 'share_whatsapp',
        totalValue,
        itemCount,
      });
    } catch (err) {
      console.error('Error sharing to WhatsApp:', err);
    } finally {
      setIsCreatingLink(false);
    }
  };

  const handleShareToMessenger = async () => {
    setIsCreatingLink(true);
    setError(null);

    try {
      const url = await getOrCreateShareUrl();

      // Facebook Messenger share URL
      const messengerUrl = Platform.OS === 'web'
        ? `https://www.facebook.com/dialog/send?link=${encodeURIComponent(url)}&redirect_uri=${encodeURIComponent(url)}`
        : `fb-messenger://share?link=${encodeURIComponent(url)}`;

      if (Platform.OS === 'web') {
        window.open(messengerUrl, '_blank', 'width=600,height=400');
      } else {
        const Linking = require('react-native').Linking;
        const canOpen = await Linking.canOpenURL(messengerUrl);
        if (canOpen) {
          await Linking.openURL(messengerUrl);
        } else {
          // Fallback to web version
          await Linking.openURL(`https://www.facebook.com/dialog/send?link=${encodeURIComponent(url)}`);
        }
      }

      trackShareEvent({
        type: 'share_messenger',
        totalValue,
        itemCount,
      });
    } catch (err) {
      console.error('Error sharing to Messenger:', err);
    } finally {
      setIsCreatingLink(false);
    }
  };

  const handleShareToSnapchat = async () => {
    setIsCreatingLink(true);
    setError(null);

    try {
      const url = await getOrCreateShareUrl();

      // Snapchat doesn't have a direct URL scheme for sharing links
      // Best approach is to use the native share sheet which includes Snapchat
      if (Platform.OS === 'web') {
        // On web, copy link and show message
        await navigator.clipboard.writeText(url);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } else {
        // On native, use the share sheet - Snapchat will appear if installed
        await Sharing.shareAsync(url, {
          dialogTitle: 'Share to Snapchat',
          mimeType: 'text/plain',
        }).catch(() => {
          // Fallback: open Snapchat app if available
          const Linking = require('react-native').Linking;
          Linking.openURL('snapchat://');
        });
      }

      trackShareEvent({
        type: 'share_snapchat',
        totalValue,
        itemCount,
      });
    } catch (err) {
      console.error('Error sharing to Snapchat:', err);
    } finally {
      setIsCreatingLink(false);
    }
  };

  const handleSaveImage = async () => {
    setIsSharing(true);
    setError(null);

    try {
      let uri: string;

      if (Platform.OS === 'web') {
        uri = await captureWebImage();

        const link = document.createElement('a');
        link.href = uri;
        link.download = 'my-cache-value.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        trackShareEvent({
          type: 'portfolio_card_save',
          totalValue,
          itemCount,
        });
      } else {
        uri = await captureNativeImage();

        const isAvailable = await Sharing.isAvailableAsync();
        if (isAvailable) {
          await Sharing.shareAsync(uri, {
            mimeType: 'image/png',
            dialogTitle: 'Save your Cache card',
            UTI: 'public.png',
          });

          trackShareEvent({
            type: 'portfolio_card_save',
            totalValue,
            itemCount,
          });
        } else {
          throw new Error('Saving is not available on this device');
        }
      }

      closeSheet();
    } catch (err) {
      console.error('Save error:', err);
      setError(err instanceof Error ? err.message : 'Failed to save image');
    } finally {
      setIsSharing(false);
    }
  };

  const renderCardContent = () => (
    <ShareCardTemplate
      totalValue={totalValue}
      itemCount={itemCount}
      topItems={topItems}
      userName={userName}
    />
  );

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={closeSheet}
      statusBarTranslucent
    >
      <View style={styles.overlay}>
        <TouchableOpacity
          style={styles.backdrop}
          activeOpacity={1}
          onPress={closeSheet}
        />

        <Animated.View
          style={[
            styles.sheet,
            { transform: [{ translateY }] },
          ]}
        >
          {/* Drag handle */}
          <View {...panResponder.panHandlers} style={styles.handleContainer}>
            <View style={styles.handle} />
          </View>

          <ScrollView
            style={styles.scrollContent}
            contentContainerStyle={styles.scrollContentContainer}
            showsVerticalScrollIndicator={false}
          >
            {/* Preview Card */}
            <View style={styles.previewContainer}>
              {Platform.OS === 'web' ? (
                <div ref={webCardRef}>
                  {renderCardContent()}
                </div>
              ) : ViewShot ? (
                <ViewShot
                  ref={viewShotRef}
                  options={{
                    format: 'png',
                    quality: 1,
                    result: 'tmpfile',
                  }}
                >
                  {renderCardContent()}
                </ViewShot>
              ) : (
                renderCardContent()
              )}
            </View>

            {/* Error message */}
            {error && (
              <Text style={styles.errorText}>{error}</Text>
            )}
          </ScrollView>

          {/* Share Options */}
          <View style={styles.shareOptions}>
            <Text style={styles.shareOptionsTitle}>Share to</Text>

            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.shareIconsContainer}
            >
              {/* Copy Link */}
              <TouchableOpacity style={styles.shareOption} onPress={handleCopyLink} disabled={isCreatingLink}>
                <View style={[styles.shareIconCircle, linkCopied && styles.shareIconCircleActive]}>
                  {isCreatingLink ? (
                    <ActivityIndicator size="small" color={theme.colors.textPrimary} />
                  ) : (
                    <Ionicons
                      name={linkCopied ? 'checkmark' : 'link'}
                      size={24}
                      color={linkCopied ? theme.colors.onAccent : theme.colors.textPrimary}
                    />
                  )}
                </View>
                <Text style={styles.shareOptionLabel}>
                  {isCreatingLink ? 'Creating...' : linkCopied ? 'Copied!' : 'Copy link'}
                </Text>
              </TouchableOpacity>

              {/* Share (opens native share sheet) */}
              <TouchableOpacity style={styles.shareOption} onPress={handleShare} disabled={isSharing}>
                <View style={styles.shareIconCircle}>
                  {isSharing ? (
                    <ActivityIndicator size="small" color={theme.colors.textPrimary} />
                  ) : (
                    <Ionicons name="share-outline" size={24} color={theme.colors.textPrimary} />
                  )}
                </View>
                <Text style={styles.shareOptionLabel}>Share</Text>
              </TouchableOpacity>

              {/* Save Image */}
              <TouchableOpacity style={styles.shareOption} onPress={handleSaveImage} disabled={isSharing}>
                <View style={styles.shareIconCircle}>
                  <Ionicons name="download-outline" size={24} color={theme.colors.textPrimary} />
                </View>
                <Text style={styles.shareOptionLabel}>Save</Text>
              </TouchableOpacity>

              {/* Messages (shares link via SMS) */}
              <TouchableOpacity style={styles.shareOption} onPress={handleShareToMessages} disabled={isCreatingLink}>
                <View style={[styles.shareIconCircle, { backgroundColor: '#34C759' }]}>
                  <Ionicons name="chatbubble-ellipses" size={24} color="#fff" />
                </View>
                <Text style={styles.shareOptionLabel}>Messages</Text>
              </TouchableOpacity>

              {/* WhatsApp (shares link via WhatsApp) */}
              <TouchableOpacity style={styles.shareOption} onPress={handleShareToWhatsApp} disabled={isCreatingLink}>
                <View style={[styles.shareIconCircle, { backgroundColor: '#25D366' }]}>
                  <Ionicons name="logo-whatsapp" size={24} color="#fff" />
                </View>
                <Text style={styles.shareOptionLabel}>WhatsApp</Text>
              </TouchableOpacity>

              {/* Facebook Messenger */}
              <TouchableOpacity style={styles.shareOption} onPress={handleShareToMessenger} disabled={isCreatingLink}>
                <View style={[styles.shareIconCircle, { backgroundColor: '#0084FF' }]}>
                  <Ionicons name="chatbubbles" size={24} color="#fff" />
                </View>
                <Text style={styles.shareOptionLabel}>Messenger</Text>
              </TouchableOpacity>

              {/* Snapchat */}
              <TouchableOpacity style={styles.shareOption} onPress={handleShareToSnapchat} disabled={isCreatingLink}>
                <View style={[styles.shareIconCircle, { backgroundColor: '#FFFC00' }]}>
                  <Ionicons name="logo-snapchat" size={24} color="#000" />
                </View>
                <Text style={styles.shareOptionLabel}>Snapchat</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
}

